import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { isValidEmail, Select } from '@aline/common';
import type { AutocompleteProps } from '@mui/material';
import { Autocomplete, ListItemText, Typography } from '@mui/material';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import type { AutocompleteChangeReason } from '@mui/material/useAutocomplete';
import type { AvatarInfo } from 'src/components';
import {
  AlineAiAvatar,
  AlineAutocompleteInput,
  AlineAvatar,
  AlineAvatarGroup,
  AlineCheckbox,
  getWorkflowCollaboratorOptionDisabled,
  getWorkflowPermissionDisplayDetails,
} from 'src/components';
import { CrossIcon } from 'src/components/icons/CrossIcon';
import { WORKFLOW_LAUNCHER_LABEL } from 'src/components/workflow/workflow-helpers';
import { Apollo } from 'src/gql/generated';
import { getEntityDisplayDetails } from 'src/helpers';
import {
  useGetOrgMembers,
  useGetRequesterUserDefinedRoles,
  useGetWorkflowPermissionsWithSteps,
} from 'src/hooks';
import { useGetOrCreateWorkflowLauncherPermission } from 'src/hooks/workflow/workflow-permission';
import { roleAvatarColor } from 'src/theme';
import type { AccountSubset, UserDefinedRoleSubset } from 'src/types';
import { getSigningStepsFromWorkflow } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

export const unassignedString = 'Unassigned';

export enum WorkflowAssignmentGroupKey {
  AlineAi = 'Aline AI',
  Collaborators = 'Workflow Collaborators',
  Contacts = 'Contacts',
  NewCollaborators = 'New Collaborators',
  Roles = 'Roles',
  Users = 'Users',
}

/** Synthetic option that assigns the task to Aline AI. Only surfaced in the WorkflowTask context. */
export interface WorkflowAlineAiOption {
  __typename: 'AlineAi';
  group: WorkflowAssignmentGroupKey.AlineAi;
}

const ALINE_AI_OPTION: WorkflowAlineAiOption = {
  __typename: 'AlineAi',
  group: WorkflowAssignmentGroupKey.AlineAi,
};

interface NewWorkflowCollaboratorOption {
  __typename: 'NewCollaborator';
  group: WorkflowAssignmentGroupKey.NewCollaborators;
  text: string;
  type: 'email' | 'role';
}
type WorkflowAssignmentUser = Pick<
  Apollo.User,
  'avatarColor' | 'displayName' | 'email' | 'id' | '__typename'
>;

interface WorkflowUserOption extends WorkflowAssignmentUser {
  group: WorkflowAssignmentGroupKey.Contacts | WorkflowAssignmentGroupKey.Users;
}
interface WorkflowRoleOption extends Pick<
  Apollo.UserDefinedRole,
  'id' | 'roleName' | '__typename'
> {
  group: WorkflowAssignmentGroupKey.Roles;
}
/**
 * Only what the picker needs of a permission: identity, the fields it displays, and the two a
 * consumer reads back off a selection. Deliberately narrower than the permissions query row so a
 * caller holding a subset — a step row, a task's permission — can pass its selection as a `value`.
 */
interface WorkflowPermissionOption extends Pick<
  Apollo.WorkflowPermission,
  '__typename' | 'id' | 'isAssignee' | 'isLauncher' | 'timeliness'
> {
  account: AccountSubset | null;
  role: UserDefinedRoleSubset | null;
}
export interface WorkflowCollaboratorOption extends WorkflowPermissionOption {
  group: WorkflowAssignmentGroupKey.Collaborators;
}
/**
 * Offered on a template until it has a launcher placeholder, listed first under Roles. Picking it
 * resolves to the real launcher permission before `onChange`, so callers never receive it.
 */
interface WorkflowLauncherOption {
  __typename: 'WorkflowLauncher';
  group: WorkflowAssignmentGroupKey.Roles;
}
export type WorkflowAssignmentOption =
  | NewWorkflowCollaboratorOption
  | WorkflowAlineAiOption
  | WorkflowCollaboratorOption
  | WorkflowLauncherOption
  | WorkflowRoleOption
  | WorkflowUserOption;
/** What `onChange` can hand a caller: the launcher option is swapped for its permission first. */
export type WorkflowAssignmentSelection = Exclude<WorkflowAssignmentOption, WorkflowLauncherOption>;

export const WORKFLOW_LAUNCHER_DESCRIPTION = 'Whoever launches this workflow';
const LAUNCHER_OPTION: WorkflowLauncherOption = {
  __typename: 'WorkflowLauncher',
  group: WorkflowAssignmentGroupKey.Roles,
};

export function getIsLauncherOption(
  option: WorkflowAssignmentOption | null
): option is WorkflowLauncherOption {
  return option?.__typename === 'WorkflowLauncher';
}

type CustomAutocompleteProps = AutocompleteProps<WorkflowAssignmentOption, false, false, false>;
type FilterOptions = CustomAutocompleteProps['filterOptions'];
export type WorkflowAssignmentValue = Exclude<
  WorkflowAssignmentSelection,
  NewWorkflowCollaboratorOption
>;

export const useStyles = makeStyles()(theme => ({
  groupChildren: {
    padding: 0,
  },
  groupHeader: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    display: 'flex',
    padding: theme.spacing(1, 2),
    position: 'sticky',
    top: -8,
    zIndex: theme.zIndex.tooltip,
  },
  groupHeaderText: {
    fontWeight: 600,
  },
  /**
   * The selected avatar sits in the input's startAdornment, so any vertical margin adds to the
   * row's height and the field grows the moment a selection appears. Keep the spacing horizontal
   * and center the avatar in the height the input already has.
   */
  input: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    margin: theme.spacing(0, 0.75),
  },
  listBox: {
    width: 400,
    [theme.breakpoints.down('sm')]: {
      width: 'auto',
    },
  },
}));

const WORKFLOW_ASSIGNMENT_GROUP_SORT_ORDER: Record<WorkflowAssignmentGroupKey, number> = {
  [WorkflowAssignmentGroupKey.AlineAi]: -1,
  [WorkflowAssignmentGroupKey.Collaborators]: 0,
  [WorkflowAssignmentGroupKey.Users]: 1,
  [WorkflowAssignmentGroupKey.Contacts]: 2,
  [WorkflowAssignmentGroupKey.Roles]: 3,
  [WorkflowAssignmentGroupKey.NewCollaborators]: 4,
};

function getWorkflowAssignmentOptionSortLabel(option: WorkflowAssignmentOption): string {
  switch (option.__typename) {
    case 'AlineAi':
      return 'Aline AI';
    case 'NewCollaborator':
      return option.text;
    case 'WorkflowPermission':
      if (option.isLauncher) return WORKFLOW_LAUNCHER_LABEL;
      return option.account?.__typename === 'Org'
        ? option.account.name
        : (option.account?.displayName ?? option.role?.roleName ?? '');
    case 'User':
      return option.displayName;
    case 'UserDefinedRole':
      return option.roleName;
    case 'WorkflowLauncher':
      return WORKFLOW_LAUNCHER_LABEL;
  }
}

export function getWorkflowAssignmentOptionIdentity(option: WorkflowAssignmentOption): string {
  switch (option.__typename) {
    case 'AlineAi':
      return 'AlineAi:aline-ai';
    case 'NewCollaborator':
      return `NewCollaborator:${option.type}:${option.text}`;
    case 'WorkflowPermission':
      return `WorkflowPermission:${option.id}`;
    case 'User':
      return `User:${option.id}`;
    case 'UserDefinedRole':
      return `UserDefinedRole:${option.id}`;
    case 'WorkflowLauncher':
      return 'WorkflowLauncher:workflow-launcher';
  }
}

export function getWorkflowAssignmentOptionEmail(option: WorkflowAssignmentOption): string | null {
  switch (option.__typename) {
    case 'User':
      return option.email;
    case 'WorkflowPermission':
      return option.account?.__typename === 'User' ? option.account.email : null;
    case 'NewCollaborator':
      return option.type === 'email' ? option.text : null;
    case 'AlineAi':
    case 'UserDefinedRole':
    case 'WorkflowLauncher':
      return null;
  }
}

/**
 * getOptionLabel is only displayName or only email depending on `emailOnly`, so MUI's
 * default filter would miss the other. Search both.
 */
const filterFunc = createFilterOptions<WorkflowAssignmentOption>({
  stringify: option => {
    const label = getWorkflowAssignmentOptionSortLabel(option);
    const email = getWorkflowAssignmentOptionEmail(option);
    if (!email || email === label) return label;

    return `${label} ${email}`;
  },
});

/** True when `input` exactly matches an existing option email (case-insensitive). */
export function getSuggestedEmailMatchesExistingOption(
  options: WorkflowAssignmentOption[],
  input: string
): boolean {
  const normalizedInput = input.trim().toLowerCase();
  if (!normalizedInput) return false;

  return options.some(option => {
    const email = getWorkflowAssignmentOptionEmail(option);
    return email?.trim().toLowerCase() === normalizedInput;
  });
}

/**
 * Appends a "New Collaborator" email row unless a visible option already has that
 * email. Callers must pass the *filtered* options — checking the full list can
 * suppress the add-new row while the match is hidden by the filter.
 */
export function appendSuggestedEmailCollaboratorIfNeeded(
  filteredOptions: WorkflowAssignmentOption[],
  input: string
): WorkflowAssignmentOption[] {
  const trimmedInput = input.trim();
  if (getSuggestedEmailMatchesExistingOption(filteredOptions, trimmedInput)) {
    return filteredOptions;
  }

  return [
    ...filteredOptions,
    {
      __typename: 'NewCollaborator',
      group: WorkflowAssignmentGroupKey.NewCollaborators,
      text: trimmedInput,
      type: 'email',
    } satisfies NewWorkflowCollaboratorOption,
  ];
}

/**
 * Orders options by group (Aline AI, collaborators, users, contacts, roles, add-new), then by label.
 * The launcher leads the Roles group.
 */
function sortWorkflowAssignmentOptionsByGroup(
  opts: WorkflowAssignmentOption[]
): WorkflowAssignmentOption[] {
  return [...opts].sort((a, b) => {
    const orderA = WORKFLOW_ASSIGNMENT_GROUP_SORT_ORDER[a.group];
    const orderB = WORKFLOW_ASSIGNMENT_GROUP_SORT_ORDER[b.group];
    if (orderA !== orderB) return orderA - orderB;
    if (getIsLauncherOption(a) !== getIsLauncherOption(b)) return getIsLauncherOption(a) ? -1 : 1;
    return getWorkflowAssignmentOptionSortLabel(a).localeCompare(
      getWorkflowAssignmentOptionSortLabel(b)
    );
  });
}

export enum WorkflowAssignmentAutocompleteContext {
  WorkflowCollaborators = 'workflow-collaborators',
  WorkflowTask = 'workflow-task',
}

interface WorkflowAssignmentPermissionIdentity {
  account: { __typename: 'User'; id: string } | { __typename: 'Org' } | null;
  role: { id: string } | null;
}

interface BuildWorkflowAssignmentOptionsArgs {
  context: WorkflowAssignmentAutocompleteContext;
  isTemplateWorkflow: boolean;
  orgContacts: WorkflowAssignmentUser[];
  orgMembers: WorkflowAssignmentUser[];
  permissions: Omit<WorkflowCollaboratorOption, 'group'>[];
  userDefinedRoles: Omit<WorkflowRoleOption, 'group'>[];
}

export function buildWorkflowAssignmentOptions({
  context,
  isTemplateWorkflow,
  orgContacts,
  orgMembers,
  permissions,
  userDefinedRoles,
}: BuildWorkflowAssignmentOptionsArgs): WorkflowAssignmentOption[] {
  const userIdsInPermissions = new Set<string>(
    permissions.flatMap(p => {
      return p.account?.__typename === 'User' && !p.role ? [p.account.id] : [];
    })
  );
  const roleIdsInPermissions = new Set<string>(
    permissions.flatMap(p => {
      return p.role && !p.account ? [p.role.id] : [];
    })
  );
  const filteredRoles = rolesToSortedRoleOptions(
    userDefinedRoles.filter(role => !roleIdsInPermissions.has(role.id))
  );
  const options: WorkflowAssignmentOption[] = [
    /**
     * "Aline AI" is only a valid assignee for workflow tasks, not for
     * collaborator/recipient/signing pickers.
     */
    ...(context === WorkflowAssignmentAutocompleteContext.WorkflowTask ? [ALINE_AI_OPTION] : []),
    ...collaboratorsToSortedCollaboratorOptions(permissions),
    ...usersToSortedUserOptions(orgMembers.filter(member => !userIdsInPermissions.has(member.id))),
    ...getContactOptionsForWorkflowAssignment({ orgContacts, orgMembers, permissions }),
  ];
  if (isTemplateWorkflow) options.push(...filteredRoles);
  // Once the template has its launcher placeholder, it is offered as a collaborator instead.
  if (isTemplateWorkflow && !permissions.some(p => p.isLauncher)) options.push(LAUNCHER_OPTION);

  return options;
}

export function getContactOptionsForWorkflowAssignment({
  orgContacts,
  orgMembers,
  permissions,
}: {
  orgContacts: WorkflowAssignmentUser[];
  orgMembers: WorkflowAssignmentUser[];
  permissions: WorkflowAssignmentPermissionIdentity[];
}): WorkflowUserOption[] {
  const userIdsInPermissions = new Set<string>(
    permissions.flatMap(p => {
      return p.account?.__typename === 'User' && !p.role ? [p.account.id] : [];
    })
  );
  const userIdsInOrgMembers = new Set(orgMembers.map(member => member.id));

  return contactsToSortedContactOptions(
    orgContacts.filter(contact => {
      return !userIdsInPermissions.has(contact.id) && !userIdsInOrgMembers.has(contact.id);
    })
  );
}

export interface WorkflowAssignmentAutocompleteProps {
  onChange: (
    event: React.ChangeEvent<unknown>,
    value: WorkflowAssignmentSelection | null,
    reason: AutocompleteChangeReason
  ) => void;
  value: WorkflowAssignmentOption | null;
  workflowId: UUID;
  autoFocus?: boolean;
  blurOnSelect?: CustomAutocompleteProps['blurOnSelect'];
  classes?: CustomAutocompleteProps['classes'];
  className?: string;
  context?: WorkflowAssignmentAutocompleteContext;
  dataCy?: string;
  disableCloseOnSelect?: boolean;
  disabled?: boolean;
  disablePortal?: boolean;
  emailOnly?: boolean;
  /** Leaves Aline AI out of a task picker, as when adding reviewers. */
  excludeAlineAi?: boolean;
  /** Options left out of the list, such as people already on the task in the other role. */
  excludedOptions?: readonly WorkflowAssignmentOption[];
  fullWidth?: boolean;
  isDelegatedToAi?: boolean;
  /** Defaults to multi-select for the task context and single-select everywhere else. */
  multiple?: boolean;
  onClose?: () => void;
  open?: boolean;
  placeholder?: string;
  refetch?: boolean;
  renderInput?: CustomAutocompleteProps['renderInput'];
  renderOption?: (option: WorkflowAssignmentOption) => React.ReactNode;
  showSelectionCheckboxes?: boolean;
  suggestNew?: boolean;
  /**
   * All current selections for the multi-select task context (a task can have
   * several assignees). Takes precedence over `value` when provided.
   */
  values?: WorkflowAssignmentOption[];
}

export function WorkflowAssignmentAutocomplete(
  props: WorkflowAssignmentAutocompleteProps
): JSX.Element | null {
  const {
    autoFocus = false,
    blurOnSelect = true,
    className,
    classes,
    context = WorkflowAssignmentAutocompleteContext.WorkflowCollaborators,
    dataCy,
    disableCloseOnSelect,
    disablePortal,
    disabled,
    emailOnly = false,
    excludeAlineAi = false,
    excludedOptions,
    fullWidth = true,
    isDelegatedToAi = false,
    multiple,
    onChange,
    onClose,
    open: controlledOpen,
    // emailOnly is destructured before placeholder, so the conditional resolves correctly.
    placeholder = emailOnly ? 'Search by email...' : 'Search assignees...',
    refetch,
    renderInput,
    renderOption,
    showSelectionCheckboxes = false,
    suggestNew = true,
    value: selectedOption,
    values: selectedOptions,
    workflowId,
  } = props;

  const { classes: internalClasses } = useStyles();
  const [inputValue, setInputValue] = useState<string>('');
  const [uncontrolledOpen, setUncontrolledOpen] = useState<boolean>(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const isTaskAssignmentAutocomplete =
    context === WorkflowAssignmentAutocompleteContext.WorkflowTask;
  const isMultiple = multiple ?? isTaskAssignmentAutocomplete;

  const {
    loading: loadingPermissions,
    permissions,
    refetch: refetchPermissions,
    workflow,
  } = useGetWorkflowPermissionsWithSteps(workflowId);
  const {
    loading: loadingRoles,
    refetch: refetchRoles,
    userDefinedRoles,
  } = useGetRequesterUserDefinedRoles();
  const {
    loading: loadingOrgMembers,
    orgContacts,
    orgMembers,
    refetch: refetchOrgMembers,
  } = useGetOrgMembers({
    variables: {
      filter: Apollo.OrgMemberFilter.Active,
    },
  });
  const { getOrCreateWorkflowLauncherPermission } = useGetOrCreateWorkflowLauncherPermission();
  const isTemplateWorkflow = workflow?.variant === Apollo.WorkflowVariant.Template;
  const options = useMemo((): WorkflowAssignmentOption[] => {
    const built = buildWorkflowAssignmentOptions({
      context,
      isTemplateWorkflow,
      orgContacts,
      orgMembers,
      permissions,
      userDefinedRoles,
    });
    const excludedIds = new Set(excludedOptions?.map(getWorkflowAssignmentOptionIdentity));
    return built.filter(
      option =>
        !(excludeAlineAi && option.__typename === 'AlineAi') &&
        !excludedIds.has(getWorkflowAssignmentOptionIdentity(option))
    );
  }, [
    context,
    excludeAlineAi,
    excludedOptions,
    isTemplateWorkflow,
    orgContacts,
    orgMembers,
    permissions,
    userDefinedRoles,
  ]);
  const selectLauncher = useCallback(
    async (event: React.SyntheticEvent) => {
      const permission = await getOrCreateWorkflowLauncherPermission(workflowId);
      if (!permission) return;

      onChange(event, toWorkflowCollaboratorOption(permission), 'selectOption');
    },
    [getOrCreateWorkflowLauncherPermission, onChange, workflowId]
  );
  /**
   * A selection is not always in `options`: it can be a typed-in email, or a permission the caller
   * holds while the list is still loading. MUI warns and drops a value it cannot find, so carry it.
   */
  const currentSelections = useMemo<WorkflowAssignmentOption[]>(
    () => selectedOptions ?? (selectedOption ? [selectedOption] : []),
    [selectedOption, selectedOptions]
  );
  const optionsWithValue = useMemo<WorkflowAssignmentOption[]>(() => {
    const optionIdentities = new Set(options.map(getWorkflowAssignmentOptionIdentity));
    const carriedSelections = currentSelections.filter(
      selection => !optionIdentities.has(getWorkflowAssignmentOptionIdentity(selection))
    );

    return carriedSelections.length > 0 ? [...options, ...carriedSelections] : options;
  }, [currentSelections, options]);
  const selectedValues = useMemo<WorkflowAssignmentOption[]>(() => {
    const values: WorkflowAssignmentOption[] = [];
    if (isDelegatedToAi) values.push(ALINE_AI_OPTION);
    values.push(...currentSelections);
    return values;
  }, [currentSelections, isDelegatedToAi]);

  const filterOptionsAndSuggestIfNeeded: FilterOptions = (options, filterOptionsState) => {
    const filteredOptions = filterFunc(options, filterOptionsState);

    if (!suggestNew) {
      return isTemplateWorkflow
        ? sortWorkflowAssignmentOptionsByGroup(filteredOptions)
        : filteredOptions;
    }
    if (isTemplateWorkflow) {
      const suggestedRoleMatchesExisting = userDefinedRoles.some(
        role => role.roleName.toLowerCase() === filterOptionsState.inputValue.toLowerCase()
      );
      if (suggestedRoleMatchesExisting)
        return sortWorkflowAssignmentOptionsByGroup(filteredOptions);

      return sortWorkflowAssignmentOptionsByGroup([
        ...filteredOptions,
        {
          __typename: 'NewCollaborator',
          group: WorkflowAssignmentGroupKey.NewCollaborators,
          text: filterOptionsState.inputValue,
          type: 'role',
        } satisfies NewWorkflowCollaboratorOption,
      ]);
    }

    const trimmedInput = filterOptionsState.inputValue.trim();
    return appendSuggestedEmailCollaboratorIfNeeded(filteredOptions, trimmedInput);
  };

  useEffect(() => {
    if (!refetch) return;
    void Promise.all([refetchOrgMembers(), refetchPermissions(), refetchRoles()]);
  }, [refetch, refetchOrgMembers, refetchPermissions, refetchRoles]);

  return (
    <Autocomplete<WorkflowAssignmentOption, boolean, false, false>
      openOnFocus
      blurOnSelect={blurOnSelect}
      className={className}
      classes={{ ...classes, paper: internalClasses.listBox }}
      data-cy={dataCy}
      disableCloseOnSelect={disableCloseOnSelect}
      disablePortal={disablePortal}
      disabled={disabled}
      filterOptions={filterOptionsAndSuggestIfNeeded}
      fullWidth={fullWidth}
      getOptionDisabled={option => {
        switch (option.__typename) {
          case 'NewCollaborator':
            return option.type === 'email' && !isValidEmail(option.text);
          default:
            return getWorkflowCollaboratorOptionDisabled({
              context,
              option,
              steps: getSigningStepsFromWorkflow(workflow),
            });
        }
      }}
      getOptionKey={getWorkflowAssignmentOptionIdentity}
      getOptionLabel={option => {
        switch (option.__typename) {
          case 'AlineAi':
            return 'Aline AI';
          case 'NewCollaborator':
            return option.text;
          case 'WorkflowPermission': {
            if (option.isLauncher) return WORKFLOW_LAUNCHER_LABEL;
            if (emailOnly) {
              if (option.account?.__typename === 'User') return option.account.email;
              return option.role?.roleName ?? '';
            }
            return option.account?.__typename === 'Org'
              ? option.account.name
              : (option.account?.displayName ?? option.role?.roleName ?? '');
          }
          case 'User':
            return emailOnly ? option.email : option.displayName;
          case 'UserDefinedRole':
            return option.roleName;
          case 'WorkflowLauncher':
            return WORKFLOW_LAUNCHER_LABEL;
        }
      }}
      groupBy={option => option.group}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value): boolean => {
        return (
          getWorkflowAssignmentOptionIdentity(option) === getWorkflowAssignmentOptionIdentity(value)
        );
      }}
      loading={loadingPermissions || loadingOrgMembers || loadingRoles}
      multiple={isMultiple}
      open={open}
      options={optionsWithValue}
      renderGroup={({ children, group, key }) => {
        const isNewGroup = group === WorkflowAssignmentGroupKey.NewCollaborators;

        return (
          <li key={key}>
            {!isNewGroup && (
              <div className={internalClasses.groupHeader}>
                <Typography className={internalClasses.groupHeaderText}>{group}</Typography>
              </div>
            )}
            <ul className={internalClasses.groupChildren}>{children}</ul>
          </li>
        );
      }}
      renderInput={params => {
        if (renderInput) return renderInput(params);

        return (
          <AlineAutocompleteInput
            {...params}
            autoFocus={autoFocus}
            placeholder={placeholder}
            startAdornment={
              <SelectedAvatars
                className={internalClasses.input}
                isDelegatedToAi={isDelegatedToAi}
                option={selectedOption}
              />
            }
            // Necessary to prevent bug where spaces aren't working for an Autocomplete inside DataGrid cell.  See https://github.com/mui/mui-x/issues/8970.
            onKeyDown={e => e.stopPropagation()}
          />
        );
      }}
      renderOption={(renderOptionProps, option, state) => {
        if (renderOption) return renderOption(option);

        const { key, ...rest } = renderOptionProps;

        return (
          <li key={key} {...rest} style={{ padding: showSelectionCheckboxes ? '4px' : '4px 12px' }}>
            <WorkflowAssignmentAutoCompleteOption
              getCollaboratorDisabled={option =>
                getWorkflowCollaboratorOptionDisabled({
                  context,
                  option,
                  steps: getSigningStepsFromWorkflow(workflow),
                })
              }
              option={option}
              selected={state.selected}
              showCheckbox={showSelectionCheckboxes}
              workflowId={workflowId}
            />
          </li>
        );
      }}
      slotProps={{
        listbox: {
          'data-cy': Select.components.workflowAssignmentAutocomplete.listBox,
        },
      }}
      value={isMultiple ? selectedValues : selectedOption}
      onChange={(event, value, reason, details) => {
        const picked = isMultiple ? (details?.option ?? null) : value;
        if (Array.isArray(picked)) return;
        if (getIsLauncherOption(picked)) {
          void selectLauncher(event);
          return;
        }

        onChange(event, picked, reason);
      }}
      onClose={() => {
        setUncontrolledOpen(false);
        onClose?.();
      }}
      onInputChange={(_, inputValue, reason) => {
        if (showSelectionCheckboxes && reason !== 'input') return;
        setInputValue(inputValue);
      }}
      onOpen={async () => {
        setUncontrolledOpen(true);
        await Promise.all([refetchOrgMembers(), refetchPermissions(), refetchRoles()]);
      }}
    />
  );
}

export const useAssignmentOptionStyles = makeStyles()(theme => ({
  icon: {
    height: 18,
    width: 18,
  },
  iconCircle: {
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    color: theme.palette.common.white,
    display: 'flex',
    justifyContent: 'center',
    // Use min/max b/c when typing long strings in the autocomplete it preserves height/width and using normal CSS height/width does not.
    maxHeight: 24,
    maxWidth: 24,
    minHeight: 24,
    minWidth: 24,
  },
  newUser: {
    ...theme.appTypography.buttonSmall,
    color: theme.appTypography.buttonSmall.color,
    whiteSpace: 'nowrap',
  },
  newUserOptionContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
  optionContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
  optionText: {
    marginBottom: 0,
    marginTop: 0,
  },
  secondaryText: {
    color: theme.appColors.secondaryType,
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
  },
}));

interface NewOptionProps {
  option: NewWorkflowCollaboratorOption;
}

export function NewOption({ option }: NewOptionProps): JSX.Element | null {
  const { classes } = useAssignmentOptionStyles();

  if (!option.text) return null;

  return (
    <div
      className={classes.newUserOptionContainer}
      data-cy={Select.components.workflowAssignmentAutocomplete.addCollaboratorButton}
    >
      <div className={classes.iconCircle}>
        <CrossIcon className={classes.icon} />
      </div>
      <Typography className={classes.newUser}>
        {option.type === 'email' ? `Add "${option.text}" email` : `Add "${option.text}" role`}
      </Typography>
    </div>
  );
}

interface WorkflowAssignmentOptionProps {
  getCollaboratorDisabled: (option: WorkflowAssignmentOption) => boolean;
  option: WorkflowAssignmentOption;
  workflowId: UUID;
  selected?: boolean;
  showCheckbox?: boolean;
}

export function WorkflowAssignmentAutoCompleteOption({
  getCollaboratorDisabled,
  option,
  selected = false,
  showCheckbox = false,
}: WorkflowAssignmentOptionProps): JSX.Element {
  const { classes } = useAssignmentOptionStyles();
  const disabled = getCollaboratorDisabled(option);
  const isNewCollaboratorOption = option.__typename === 'NewCollaborator';

  if (isNewCollaboratorOption) return <NewOption option={option} />;

  const { primaryText, secondaryText } = getOptionDisplayDetails(option);

  return (
    <div
      className={classes.optionContainer}
      data-cy={Select.components.workflowAssignmentAutocomplete.collaboratorOption}
    >
      {showCheckbox && (
        <AlineCheckbox checked={selected} disabled={disabled} size="small" tabIndex={-1} />
      )}
      <OptionAvatar option={option} />
      <ListItemText
        className={classes.optionText}
        classes={{ secondary: classes.secondaryText }}
        data-cy="collaborator-option-text"
        primary={primaryText}
        secondary={secondaryText}
        slotProps={{
          primary: { align: 'left', noWrap: true, variant: 'body1' },
          secondary: { align: 'left', noWrap: true },
        }}
      />
      {disabled && (
        <Typography noWrap variant="caption">
          No Available Actions
        </Typography>
      )}
    </div>
  );
}

function SelectedAvatars({
  className,
  isDelegatedToAi,
  option,
}: {
  isDelegatedToAi: boolean;
  option: WorkflowAssignmentOption | null;
  className?: string;
}): JSX.Element | null {
  if (!option && !isDelegatedToAi) return null;

  return (
    <span className={className}>
      <AlineAvatarGroup>
        {option && <OptionAvatar option={option} />}
        {isDelegatedToAi && <OptionAvatar option={ALINE_AI_OPTION} />}
      </AlineAvatarGroup>
    </span>
  );
}

function OptionAvatar({
  className,
  option,
}: {
  option: WorkflowAssignmentOption | null;
  className?: string;
}): JSX.Element {
  if (option?.__typename === 'AlineAi') {
    return <AlineAiAvatar className={className} />;
  }

  const { avatarColor, avatarInfo } = getOptionDisplayDetails(option);

  return (
    <AlineAvatar
      avatarInfo={avatarInfo}
      className={className}
      styles={{ backgroundColor: avatarColor }}
    />
  );
}

export function getOptionDisplayDetails(option: WorkflowAssignmentOption | null): {
  avatarInfo: AvatarInfo;
  primaryText: string;
  avatarColor?: string;
  secondaryText?: string;
} {
  if (!option) return { avatarInfo: null, primaryText: unassignedString };

  switch (option.__typename) {
    case 'AlineAi':
      return { avatarInfo: null, primaryText: 'Aline AI' };
    /**
     * Reachable only as a selected value: a caller holding its own selection can be showing an
     * email that was typed rather than picked, which has no account to draw an avatar from.
     */
    case 'NewCollaborator':
      return { avatarInfo: option.text.charAt(0).toUpperCase(), primaryText: option.text };
    case 'User': {
      const isDisplayNameDiffThanEmail =
        option.displayName.toLowerCase() !== option.email.toLowerCase();

      return {
        avatarColor: option.avatarColor,
        avatarInfo: option,
        primaryText: option.displayName,
        secondaryText: isDisplayNameDiffThanEmail ? option.email : undefined,
      };
    }
    case 'UserDefinedRole':
      return {
        avatarColor: roleAvatarColor,
        avatarInfo: option,
        primaryText: option.roleName,
      };
    case 'WorkflowLauncher':
      return {
        avatarColor: roleAvatarColor,
        avatarInfo: WORKFLOW_LAUNCHER_LABEL.charAt(0),
        primaryText: WORKFLOW_LAUNCHER_LABEL,
        secondaryText: WORKFLOW_LAUNCHER_DESCRIPTION,
      };
    case 'WorkflowPermission':
      return getWorkflowPermissionDisplayDetails(option);
  }
}

/** Wraps a permission a caller already holds as the option shape `value` expects. */
export function toWorkflowCollaboratorOption(
  permission: Omit<WorkflowCollaboratorOption, 'group'>
): WorkflowCollaboratorOption {
  return { ...permission, group: WorkflowAssignmentGroupKey.Collaborators };
}

export function collaboratorsToSortedCollaboratorOptions(
  collaborators: Omit<WorkflowCollaboratorOption, 'group'>[]
): WorkflowCollaboratorOption[] {
  return collaborators.map(toWorkflowCollaboratorOption).sort((a, b) => {
    const aName = a.account
      ? getEntityDisplayDetails(a.account).primary
      : (a.role?.roleName ?? unassignedString);
    const bName = b.account
      ? getEntityDisplayDetails(b.account).primary
      : (b.role?.roleName ?? unassignedString);
    return aName.localeCompare(bName);
  });
}

export function usersToSortedUserOptions(
  users: Omit<WorkflowUserOption, 'group'>[]
): WorkflowUserOption[] {
  return users
    .map(
      (user): WorkflowUserOption => ({
        ...user,
        group: WorkflowAssignmentGroupKey.Users,
      })
    )
    .sort((a, b) => {
      return a.displayName.localeCompare(b.displayName);
    });
}

export function contactsToSortedContactOptions(
  users: Omit<WorkflowUserOption, 'group'>[]
): WorkflowUserOption[] {
  return users
    .map(
      (user): WorkflowUserOption => ({
        ...user,
        group: WorkflowAssignmentGroupKey.Contacts,
      })
    )
    .sort((a, b) => {
      return a.displayName.localeCompare(b.displayName);
    });
}

export function rolesToSortedRoleOptions(
  roles: Omit<WorkflowRoleOption, 'group'>[]
): WorkflowRoleOption[] {
  return roles
    .map(
      (role): WorkflowRoleOption => ({
        ...role,
        group: WorkflowAssignmentGroupKey.Roles,
      })
    )
    .sort((a, b) => {
      return a.roleName.localeCompare(b.roleName);
    });
}
